/**
 * @spec docs/specs/features/staking-ui-batched-reads.md (R10)
 *
 * The app's Base JSON-RPC provider. Every browser Base read goes to rewards-api's keyless
 * `/rpc/base` relay (read methods only, batches of at most 5), so no RPC key ships in the bundle.
 * The network is static, so ethers never probes `eth_chainId`. Polygon balances come from
 * `POST /read/balances` (lib/read-api.ts); the one browser Polygon provider, for transaction
 * receipts only, is in lib/tx-tracker/read-providers.ts (R10 amendment, 2026-10-15).
 *
 * Transactions are sent through the wallet; their confirmation is read by the tracker
 * (lib/tx-tracker/), never through the wallet.
 */
import { ethers } from 'ethers';
import { env } from '@/lib/env';
import { getStakingChainId } from '@/lib/chains';

/** The relay accepts at most this many entries per JSON-RPC batch. */
const RELAY_BATCH_MAX = 5;

let cachedProvider: ethers.JsonRpcProvider | null = null;

/** URL of the rewards-api Base relay; never used as chain metadata. */
export function baseRelayUrl(): string {
  const apiUrl = env.rewardsApiUrl;
  if (!apiUrl) {
    throw new Error('Rewards API URL not configured');
  }
  return `${apiUrl.replace(/\/$/, '')}/rpc/base`;
}

export function getBaseProvider(): ethers.JsonRpcProvider {
  if (!cachedProvider) {
    const network = ethers.Network.from(getStakingChainId());
    cachedProvider = new ethers.JsonRpcProvider(baseRelayUrl(), network, {
      staticNetwork: network,
      batchMaxCount: RELAY_BATCH_MAX,
    });
  }
  return cachedProvider;
}

/** Reset the cached provider (for test isolation) */
export function resetProvider(): void {
  cachedProvider = null;
}
